/**
 * @file Timer view — clock in/out, breaks, customer/job selector, time log
 */

import {
  getActiveTimer, startTimer, stopTimer, pauseTimer, resumeTimer,
  startBreak, endBreak, getTodaysTimeLogs, formatElapsed, msToHours
} from '@services/timer.js';
import { getActiveJobs, getJobCustomer, getJobs } from '@services/jobs.js';
import { load } from '@state/store.js';
import { toast } from '@components/toast.js';
import { showModal, closeModal } from '@components/modal.js';

let _tickInterval = null;
let _selectedCustomerId = '';

function render() {
  const timer = getActiveTimer();
  const todaysLogs = getTodaysTimeLogs();
  const totalToday = todaysLogs.reduce((sum, l) => sum + (l.duration || 0), 0);

  if (timer) {
    return renderActiveTimer(timer, todaysLogs, totalToday);
  }

  return renderTimerSetup(todaysLogs, totalToday);
}

function renderActiveTimer(timer, todaysLogs, totalToday) {
  const pauseMs = timer.paused && timer.pauseStart ? Date.now() - timer.pauseStart : 0;
  const elapsed = Math.max(0, Date.now() - timer.startTime - (timer.breakDuration || 0) - pauseMs);
  const isOnBreak = timer.onBreak || (timer.paused && timer.breaks && timer.breaks.length > 0 && !timer.breaks[timer.breaks.length - 1].end);

  return `
    <div class="view-header">
      <h1>Timer</h1>
    </div>

    <div class="timer-display timer-active">
      <div class="timer-time" id="timer-time">${formatElapsed(elapsed)}</div>
      <div class="timer-label">${esc(timer.jobDesc || 'Working')}</div>
      <div class="timer-label text-muted">${esc(timer.customerName || '')}</div>
      ${timer.paused && isOnBreak ? '<div class="badge badge-orange mt-sm">ON BREAK</div>' : ''}
      ${timer.paused && !isOnBreak ? '<div class="badge badge-orange mt-sm">PAUSED</div>' : ''}
      ${timer.breakDuration > 0 ? `<div class="text-muted text-xs mt-sm">Break time: ${formatElapsed(timer.breakDuration + (isOnBreak && timer.pauseStart ? Date.now() - timer.pauseStart : 0))}</div>` : ''}
    </div>

    <div class="timer-controls">
      ${timer.paused
        ? `<button class="btn btn-gold timer-ctrl-btn" id="timer-resume">${isOnBreak ? 'End Break' : 'Resume'}</button>`
        : `<button class="btn btn-outline timer-ctrl-btn" id="timer-pause">Pause</button>`
      }
      ${!timer.paused
        ? `<button class="btn btn-outline timer-ctrl-btn" id="timer-break">Break</button>`
        : ''
      }
      <button class="btn btn-danger timer-ctrl-btn" id="timer-stop">Stop</button>
    </div>

    ${renderTimeLogs(todaysLogs, totalToday)}
  `;
}

function renderTimerSetup(todaysLogs, totalToday) {
  const customers = load('customers', []);
  const allJobs = getJobs().filter(j =>
    j.stage !== 'Paid & Closed' &&
    j.stage !== 'Proposal Rejected'
  );

  // Filter jobs by selected customer
  const filteredJobs = _selectedCustomerId
    ? allJobs.filter(j => j.customerId === _selectedCustomerId)
    : allJobs;

  return `
    <div class="view-header">
      <h1>Timer</h1>
    </div>

    <div class="timer-display">
      <div class="timer-time" style="color:var(--gray-500);">00:00:00</div>
      <div class="timer-label">Select a job to start</div>
    </div>

    <div class="card mb-md">
      <div class="form-group">
        <label>Customer</label>
        <select id="timer-customer-select">
          <option value="">All Customers</option>
          ${customers.map(c => {
            const name = c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Unknown';
            return `<option value="${esc(c.id)}" ${c.id === _selectedCustomerId ? 'selected' : ''}>${esc(name)}</option>`;
          }).join('')}
        </select>
      </div>

      <div class="form-group">
        <label>Job</label>
        <select id="timer-job-select">
          <option value="">Select a job...</option>
          ${filteredJobs.map(j => {
            const cust = getJobCustomer(j);
            const custName = cust ? (cust.name || cust.firstName || '') : '';
            return `<option value="${esc(j.id)}" data-customer-id="${esc(j.customerId || '')}" data-customer-name="${esc(custName)}" data-job-desc="${esc(j.desc || j.name || '')}">${esc(j.desc || j.name || 'Job')} ${custName ? '- ' + esc(custName) : ''} (${esc(j.stage)})</option>`;
          }).join('')}
        </select>
      </div>

      <button class="btn btn-gold btn-full start-timer-btn" id="timer-start">Start Timer</button>
    </div>

    ${renderTimeLogs(todaysLogs, totalToday)}
  `;
}

function renderTimeLogs(logs, totalMs) {
  return `
    <div class="flex-between mb-sm">
      <h3 style="color:var(--gold);font-size:0.85rem;text-transform:uppercase;">Today's Log</h3>
      <span class="text-muted text-sm">${msToHours(totalMs)}h total</span>
    </div>
    ${logs.length > 0
      ? logs.map(log => `
        <div class="list-item">
          <div style="flex:1;min-width:0;">
            <div style="font-weight:500;font-size:0.85rem;">${esc(log.jobDesc || 'Work')}</div>
            <div class="text-muted text-xs">${esc(log.customerName || '')}</div>
            <div class="text-muted text-xs">${formatTimeRange(log.startTime, log.endTime)}</div>
          </div>
          <div style="font-weight:600;font-variant-numeric:tabular-nums;color:var(--gold);">${formatElapsed(log.duration)}</div>
        </div>
      `).join('')
      : '<div class="text-muted text-sm text-center" style="padding:20px;">No time logged today</div>'}
  `;
}

function formatTimeRange(start, end) {
  if (!start || !end) return '';
  try {
    const s = new Date(start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const e = new Date(end).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${s} - ${e}`;
  } catch (err) { return ''; }
}

function mount() {
  const timer = getActiveTimer();

  if (timer) {
    mountActiveTimer();
  } else {
    mountTimerSetup();
  }
}

function mountActiveTimer() {
  // Pause
  document.getElementById('timer-pause')?.addEventListener('click', () => {
    pauseTimer();
    toast('Timer paused', 'info');
    rerender();
  });

  // Resume (also ends break)
  document.getElementById('timer-resume')?.addEventListener('click', () => {
    const t = getActiveTimer();
    if (t && t.breaks && t.breaks.length > 0 && !t.breaks[t.breaks.length - 1].end) {
      endBreak();
      toast('Break ended', 'success');
    } else {
      resumeTimer();
      toast('Timer resumed', 'success');
    }
    rerender();
  });

  // Break
  document.getElementById('timer-break')?.addEventListener('click', () => {
    startBreak();
    toast('Break started - take a breather', 'info');
    rerender();
  });

  // Stop with confirmation
  document.getElementById('timer-stop')?.addEventListener('click', () => {
    showModal('Stop Timer', '<p>Stop the timer and log this time entry?</p>', [
      { label: 'Cancel', onClick: closeModal },
      { label: 'Stop & Log', type: 'gold', onClick: () => {
        const entry = stopTimer();
        closeModal();
        if (entry) toast(`Logged ${formatElapsed(entry.duration)}`, 'success');
        rerender();
      }},
    ]);
  });

  // Tick every second
  _tickInterval = setInterval(() => {
    const t = getActiveTimer();
    if (!t) { clearInterval(_tickInterval); return; }
    const pauseMs = t.paused && t.pauseStart ? Date.now() - t.pauseStart : 0;
    const elapsed = Math.max(0, Date.now() - t.startTime - (t.breakDuration || 0) - pauseMs);
    const el = document.getElementById('timer-time');
    if (el) el.textContent = formatElapsed(elapsed);
  }, 1000);
}

function mountTimerSetup() {
  // Customer dropdown filters job dropdown
  document.getElementById('timer-customer-select')?.addEventListener('change', (e) => {
    _selectedCustomerId = e.target.value;
    rerender();
  });

  // Start timer
  document.getElementById('timer-start')?.addEventListener('click', () => {
    const sel = document.getElementById('timer-job-select');
    if (!sel || !sel.value) {
      toast('Select a job first', 'warning');
      return;
    }
    const opt = sel.selectedOptions[0];
    startTimer(
      opt.dataset.customerId,
      opt.dataset.customerName,
      sel.value,
      opt.dataset.jobDesc
    );
    toast('Timer started', 'success');
    rerender();
  });
}

function rerender() {
  const container = document.getElementById('view-container');
  if (container) {
    container.innerHTML = render();
    mount();
  }
}

function unmount() {
  if (_tickInterval) {
    clearInterval(_tickInterval);
    _tickInterval = null;
  }
}

function esc(str) {
  if (!str) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export default { render, mount, unmount };
